import { useCallback, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { motion } from '@/utils/motion'
import { RATING_COLOR, RATING_LABEL } from '../ratingColors'
import { RatingPicker } from '../RatingPicker'
import {
  W,
  H,
  PAD,
  PLOT_W,
  FLOOR_Y,
  MAX_HISTORY,
  Y_TICKS,
  areaPath,
  curvePath,
  lerp,
  place,
  relativeDay,
  scalesFor,
  spanLabel,
  timeTicks,
  type LabNode,
  type Point,
} from './labGeometry'

/**
 * The scheduling lab's timeline: a word's strength over real time.
 *
 * Each node is a review, placed on the day it happens and at the height of the
 * interval it earned — so a lapse is a visible fall and a good run a climb that
 * stretches further apart as it rises. Everything left of today is history
 * (muted); everything right of it is the plan under the candidate settings
 * (lit), with the same plan under the saved settings as a dashed ghost, so any
 * slider move shows its before and after at once.
 *
 * Geometry is animated on the DOM directly through `motion.tween`: every change
 * morphs from the frame on screen to the new one, and the first paint draws the
 * curve in and pops the nodes up in sequence. React owns structure; the paint
 * function owns coordinates.
 *
 * Clicking a planned review opens a rating picker that scripts it in place, so
 * a plan with several slips and recoveries is built point by point on the
 * picture itself.
 */

/** Slots per series, so frames of different lengths can morph into each other. */
const SLOTS = 1 + MAX_HISTORY + 24
const GHOST_SLOTS = 24

interface Frame {
  points: Point[]
  alive: number[]
  ghost: Point[]
  ghostAlive: number[]
  todayX: number
  masteryY: number
  knowWellY: number
}

function pad(points: Point[], slots: number, fallback: Point) {
  const tail = points[points.length - 1] ?? fallback
  return {
    points: Array.from({ length: slots }, (_, i) => points[i] ?? tail),
    alive: Array.from({ length: slots }, (_, i) => (i < points.length ? 1 : 0)),
  }
}

function blend(from: Frame, to: Frame, t: number): Frame {
  const mix = (a: Point[], b: Point[]) =>
    b.map((p, i) => ({ x: lerp(a[i]?.x ?? p.x, p.x, t), y: lerp(a[i]?.y ?? p.y, p.y, t) }))
  const fade = (a: number[], b: number[]) => b.map((v, i) => lerp(a[i] ?? v, v, t))
  return {
    points: mix(from.points, to.points),
    alive: fade(from.alive, to.alive),
    ghost: mix(from.ghost, to.ghost),
    ghostAlive: fade(from.ghostAlive, to.ghostAlive),
    todayX: lerp(from.todayX, to.todayX, t),
    masteryY: lerp(from.masteryY, to.masteryY, t),
    knowWellY: lerp(from.knowWellY, to.knowWellY, t),
  }
}

function nodeLabel(node: LabNode) {
  if (node.kind === 'origin') return `Starting strength, ${spanLabel(node.interval)}`
  const when = relativeDay(node.day)
  const rating = RATING_LABEL[node.rating ?? 3]
  const mastered = node.status === 'mastered' ? ', mastered' : ''
  const scripted = node.kind === 'plan' && !node.scripted ? ' (assumed)' : ''
  return `${node.kind === 'past' ? 'Past review' : `Review ${node.index + 1}`}, ${when}: ${rating}${scripted}, next in ${spanLabel(node.interval)}${mastered}`
}

interface LabTimelineProps {
  nodes: LabNode[]
  ghost: LabNode[]
  masteryThreshold: number
  knowWellInterval: number
  activeKey: string | null
  onActivate: (key: string) => void
  /** Scripts plan review `index` (its position in `plan.steps`) as `rating`. */
  onRate: (index: number, rating: number) => void
}

export function LabTimeline({
  nodes,
  ghost,
  masteryThreshold,
  knowWellInterval,
  activeKey,
  onActivate,
  onRate,
}: LabTimelineProps) {
  const uid = `lab${useId().replace(/[^a-zA-Z0-9]/g, '')}`
  const svgRef = useRef<SVGSVGElement>(null)
  const shown = useRef<Frame | null>(null)
  const hitRefs = useRef<(SVGCircleElement | null)[]>([])
  const opener = useRef<SVGCircleElement | null>(null)
  const [picking, setPicking] = useState<number | null>(null)

  const scales = useMemo(() => scalesFor(nodes, ghost, masteryThreshold), [nodes, ghost, masteryThreshold])
  const points = useMemo(() => place(nodes, scales), [nodes, scales])
  const ghostPoints = useMemo(() => place(ghost, scales), [ghost, scales])
  const todayX = scales.x(0)
  const lastPlan = nodes[nodes.length - 1]
  const mastered = lastPlan?.status === 'mastered'
  const activeIndex = nodes.findIndex((node) => node.key === activeKey)
  const active = activeIndex >= 0 ? points[activeIndex] : null

  useLayoutEffect(() => {
    const svg = svgRef.current
    if (!svg) return
    const floor = { x: PAD.left, y: FLOOR_Y }
    const series = pad(points, SLOTS, floor)
    const ghostSeries = pad(ghostPoints, GHOST_SLOTS, floor)
    const target: Frame = {
      points: series.points,
      alive: series.alive,
      ghost: ghostSeries.points,
      ghostAlive: ghostSeries.alive,
      todayX,
      masteryY: scales.y(masteryThreshold),
      knowWellY: scales.y(knowWellInterval),
    }

    const set = (selector: string, attrs: Record<string, string | number>) =>
      svg.querySelectorAll(selector).forEach((el) => {
        Object.entries(attrs).forEach(([name, value]) => el.setAttribute(name, `${value}`))
      })

    const paint = (frame: Frame, pop = 1) => {
      const line = curvePath(frame.points)
      set('[data-line]', { d: line })
      set('[data-area]', { d: areaPath(frame.points) })
      set('[data-ghost]', { d: curvePath(frame.ghost), opacity: Math.max(...frame.ghostAlive) })
      set('[data-past]', { width: Math.max(0, frame.todayX - PAD.left) })
      set('[data-today]', { x1: frame.todayX, x2: frame.todayX })
      set('[data-today-label]', { transform: `translate(${frame.todayX} ${FLOOR_Y + 15})` })
      set('[data-mastery]', { y1: frame.masteryY, y2: frame.masteryY })
      set('[data-mastery-band]', { height: Math.max(0, frame.masteryY - PAD.top) })
      set('[data-mastery-label]', { y: frame.masteryY - 7 })
      set('[data-know-well]', { y1: frame.knowWellY, y2: frame.knowWellY })
      set('[data-know-well-label]', { y: frame.knowWellY - 7 })
      const split = Math.min(1, Math.max(0, (frame.todayX - PAD.left) / PLOT_W))
      set(`#${uid}-stroke [data-split]`, { offset: split })
      frame.points.forEach((p, i) => {
        const scale = Math.min(1, Math.max(0, pop * 1.8 - (i / SLOTS) * 0.8))
        set(`[data-node="${i}"]`, {
          transform: `translate(${p.x.toFixed(1)} ${p.y.toFixed(1)}) scale(${scale.toFixed(3)})`,
          opacity: frame.alive[i],
        })
      })
      frame.ghost.forEach((p, i) => {
        set(`[data-ghost-dot="${i}"]`, { cx: p.x, cy: p.y, opacity: frame.ghostAlive[i] * 0.7 })
      })
    }

    const from = shown.current
    shown.current = target
    if (!from) {
      paint(target, 0)
      void motion.drawIn(svg.querySelectorAll<SVGPathElement>('[data-line], [data-ghost]'), { ms: 1100 })
      void motion.tween((t) => paint(target, t), { ms: 1100, key: svg })
      return
    }
    void motion.tween((t) => paint(blend(from, target, t)), { ms: 520, key: svg })
  }, [points, ghostPoints, todayX, scales, masteryThreshold, knowWellInterval, uid])

  const closePicker = useCallback(() => {
    setPicking(null)
    opener.current?.focus()
  }, [])

  function pick(index: number, target: SVGCircleElement) {
    onActivate(nodes[index].key)
    if (nodes[index].kind !== 'plan') return
    opener.current = target
    setPicking(index)
  }

  function rate(rating: number) {
    if (picking !== null) onRate(nodes[picking].index, rating)
    closePicker()
  }

  function handleKeyDown(event: KeyboardEvent<SVGCircleElement>, index: number) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      pick(index, event.currentTarget)
      return
    }
    const moves: Record<string, number> = {
      ArrowRight: index + 1,
      ArrowLeft: index - 1,
      Home: 0,
      End: nodes.length - 1,
    }
    const next = moves[event.key]
    if (next === undefined || next < 0 || next >= nodes.length) return
    event.preventDefault()
    onActivate(nodes[next].key)
    hitRefs.current[next]?.focus()
  }

  const yTicks = Y_TICKS.filter((tick) => tick.days <= scales.ceiling)
  const xTicks = timeTicks(scales)
  const tabbable = activeIndex >= 0 ? activeIndex : nodes.findIndex((node) => node.kind === 'plan')

  return (
    <div className="relative">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full overflow-visible font-mono select-none"
        role="group"
        aria-label="Strength over time: past reviews, then the plan under the current sliders"
      >
        <defs>
          <linearGradient id={`${uid}-stroke`} x1={PAD.left} x2={PAD.left + PLOT_W} y1={0} y2={0} gradientUnits="userSpaceOnUse">
            <stop offset={0} stopColor="var(--color-muted)" />
            <stop data-split offset={0} stopColor="var(--color-muted)" />
            <stop data-split offset={0} stopColor="var(--color-accent)" />
            {/* The plan ripens from emerald into gold as the word grows stronger. */}
            <stop offset={1} stopColor="var(--color-cefr-b1-bg)" />
          </linearGradient>
          <linearGradient id={`${uid}-area`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="var(--color-accent)" stopOpacity={0.28} />
            <stop offset="1" stopColor="var(--color-accent)" stopOpacity={0} />
          </linearGradient>
          <linearGradient id={`${uid}-band`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="var(--color-accent)" stopOpacity={0} />
            <stop offset="1" stopColor="var(--color-accent)" stopOpacity={0.14} />
          </linearGradient>
          <filter id={`${uid}-glow`} x="-20%" y="-40%" width="140%" height="180%">
            <feGaussianBlur stdDeviation={5} />
          </filter>
          <filter id={`${uid}-halo`} x="-150%" y="-150%" width="400%" height="400%">
            <feGaussianBlur stdDeviation={4} />
          </filter>
        </defs>

        {/* History sits on a slightly lifted surface, so past and plan read apart at a glance. */}
        <rect data-past x={PAD.left} y={PAD.top} height={FLOOR_Y - PAD.top} width={0} fill="var(--color-surface-bg)" opacity={0.55} rx={4} />
        <rect data-mastery-band x={PAD.left} y={PAD.top} width={PLOT_W} height={0} fill={`url(#${uid}-band)`} />

        {yTicks.map((tick) => (
          <g key={tick.days}>
            <line x1={PAD.left} x2={PAD.left + PLOT_W} y1={scales.y(tick.days)} y2={scales.y(tick.days)} stroke="var(--color-line-soft)" />
            <text x={PAD.left - 10} y={scales.y(tick.days) + 3.5} textAnchor="end" fontSize={10} fill="var(--color-muted)">
              {tick.label}
            </text>
          </g>
        ))}
        <line x1={PAD.left} x2={PAD.left + PLOT_W} y1={FLOOR_Y} y2={FLOOR_Y} stroke="var(--color-border-divider)" />
        {xTicks.map((tick) => (
          <g key={tick.day}>
            <line x1={tick.x} x2={tick.x} y1={FLOOR_Y} y2={FLOOR_Y + 5} stroke="var(--color-border-divider)" />
            <text x={tick.x} y={FLOOR_Y + 19} textAnchor="middle" fontSize={10} fill="var(--color-muted)">
              {tick.label}
            </text>
          </g>
        ))}
        <text x={PAD.left - 10} y={PAD.top - 12} textAnchor="end" fontSize={8.5} letterSpacing={1.5} fill="var(--color-muted)">
          NEXT IN
        </text>

        <line data-know-well x1={PAD.left} x2={PAD.left + PLOT_W} stroke="var(--color-muted)" strokeDasharray="2 5" opacity={0.6} />
        <text data-know-well-label x={PAD.left + 4} textAnchor="start" fontSize={9} letterSpacing={1.5} fill="var(--color-muted)" stroke="var(--color-page-bg)" strokeWidth={3} paintOrder="stroke">
          KNOW WELL · {knowWellInterval}D
        </text>
        <line data-mastery x1={PAD.left} x2={PAD.left + PLOT_W} stroke="var(--color-accent)" strokeDasharray="6 5" strokeWidth={1.25} />
        <text data-mastery-label x={PAD.left + 4} textAnchor="start" fontSize={9} letterSpacing={1.5} fill="var(--color-accent)" stroke="var(--color-page-bg)" strokeWidth={3} paintOrder="stroke">
          MASTERY · {masteryThreshold}D
        </text>

        <line data-today y1={PAD.top} y2={FLOOR_Y} stroke="var(--color-em)" strokeWidth={1} opacity={0.8} />
        <g data-today-label>
          <rect x={-24} y={-8} width={48} height={16} rx={8} fill="var(--color-em)" />
          <text y={3.5} textAnchor="middle" fontSize={8.5} letterSpacing={1.5} fill="var(--color-page-bg)">
            TODAY
          </text>
        </g>

        <path data-area fill={`url(#${uid}-area)`} />
        <path data-ghost fill="none" stroke="var(--color-muted)" strokeWidth={1.25} strokeDasharray="4 5" strokeLinecap="round" />
        {Array.from({ length: GHOST_SLOTS }, (_, i) => (
          <circle key={i} data-ghost-dot={i} r={2.2} fill="var(--color-muted)" opacity={0} />
        ))}
        <path data-line fill="none" stroke={`url(#${uid}-stroke)`} strokeWidth={6} opacity={0.35} filter={`url(#${uid}-glow)`} />
        <path data-line fill="none" stroke={`url(#${uid}-stroke)`} strokeWidth={2.25} strokeLinecap="round" />

        {active && (
          <g pointerEvents="none">
            <line x1={active.x} x2={active.x} y1={active.y} y2={FLOOR_Y} stroke="var(--color-em)" strokeDasharray="2 3" opacity={0.7} />
            <line x1={PAD.left} x2={active.x} y1={active.y} y2={active.y} stroke="var(--color-em)" strokeDasharray="2 3" opacity={0.4} />
          </g>
        )}

        {Array.from({ length: SLOTS }, (_, i) => {
          const node = nodes[i]
          const color = node?.rating ? RATING_COLOR[node.rating] : 'var(--color-muted)'
          const isActive = i === activeIndex
          const isLast = node && node === lastPlan && mastered
          const lead = node?.kind === 'plan' && node.scripted
          return (
            <g key={i} data-node={i} opacity={0} style={{ color }}>
              {isLast && (
                <circle r={13} fill="currentColor" opacity={0.55} filter={`url(#${uid}-halo)`} className="motion-safe:animate-pulse" />
              )}
              {isActive && <circle r={12} fill="none" stroke="var(--color-em)" strokeWidth={1.5} />}
              {node?.kind === 'past' ? (
                <circle r={4.5} fill="currentColor" opacity={0.8} />
              ) : node?.kind === 'origin' ? (
                <circle r={4} fill="var(--color-page-bg)" stroke="var(--color-muted)" strokeWidth={1.5} />
              ) : (
                <>
                  <circle r={lead ? 6.5 : 4.5} fill="var(--color-page-bg)" stroke="currentColor" strokeWidth={lead ? 2.5 : 1.5} />
                  {lead && <circle r={2.2} fill="currentColor" />}
                </>
              )}
              {node && (
                <circle
                  ref={(el) => {
                    hitRefs.current[i] = el
                  }}
                  r={15}
                  fill="transparent"
                  role="button"
                  tabIndex={i === tabbable ? 0 : -1}
                  aria-label={nodeLabel(node)}
                  aria-pressed={isActive}
                  aria-haspopup={node.kind === 'plan' ? 'dialog' : undefined}
                  aria-expanded={node.kind === 'plan' ? picking === i : undefined}
                  className="cursor-pointer outline-none"
                  onMouseEnter={() => onActivate(node.key)}
                  onFocus={() => onActivate(node.key)}
                  onClick={(event) => pick(i, event.currentTarget)}
                  onKeyDown={(event) => handleKeyDown(event, i)}
                />
              )}
            </g>
          )
        })}
      </svg>

      {active && nodes[activeIndex] && (
        <div
          aria-hidden
          className="pointer-events-none absolute -translate-x-1/2 -translate-y-full rounded-md border border-border-divider bg-card/95 px-3 py-1.5 font-mono text-xs whitespace-nowrap text-body shadow-sm backdrop-blur"
          style={{ left: `${(active.x / W) * 100}%`, top: `calc(${(active.y / H) * 100}% - 18px)` }}
        >
          {relativeDay(nodes[activeIndex].day)} · next in {spanLabel(nodes[activeIndex].interval)}
        </div>
      )}

      {picking !== null && nodes[picking] && points[picking] && (
        <RatingPicker
          at={{ x: points[picking].x / W, y: points[picking].y / H }}
          title={`Review ${nodes[picking].index + 1}, ${relativeDay(nodes[picking].day)}`}
          review={nodes[picking].index + 1}
          selected={nodes[picking].rating ?? 3}
          onRate={rate}
          onClose={closePicker}
        />
      )}
    </div>
  )
}
